import { isBrowser } from 'ws-scripts/modules/environment';

/**
 * Returns `window[type]` if it's safe to access, or `null` otherwise. Never throws.
 *
 * @param {string} type Either 'sessionStorage' or 'localStorage'.
 * @returns {Storage|null}
 */
const getStorage = (type) => {
	if (!isBrowser) {
		return null;
	}
	try {
		return window[type] || null;
	} catch (err) {
		// Accessing the storage object itself can throw (e.g. SecurityError).
		return null;
	}
};

/**
 * Safely reads a value from browser storage. Returns `null` if storage is
 * unavailable, missing, or the read throws for any reason.
 *
 * @param {string} type Either 'sessionStorage' or 'localStorage'.
 * @param {string} key
 * @returns {string|null}
 */
export const safeGetItem = (type, key) => {
	try {
		return getStorage(type)?.getItem(key) ?? null;
	} catch (err) {
		return null;
	}
};

/**
 * Safely writes a value to browser storage. Fails silently (returns `false`)
 * if storage is unavailable or the write throws (e.g. quota exceeded).
 *
 * @param {string} type Either 'sessionStorage' or 'localStorage'.
 * @param {string} key
 * @param {string} value
 * @returns {boolean} Whether the write succeeded.
 */
export const safeSetItem = (type, key, value) => {
	try {
		const storage = getStorage(type);
		if (!storage) {
			return false;
		}
		storage.setItem(key, value);
		return true;
	} catch (err) {
		return false;
	}
};

/**
 * Returns a JavaScript object parsed from JSON stored as a string in browser storage
 *
 * @param {string} type A string that is either 'sessionStorage' or 'localStorage' to identify the type of storage you wish to access.
 * @param {string} key A string that is the key of the browser storage item you wish to retrieve.
 *
 * @returns {object} A JavaScript object parsed from the JSON object found in browser storage
 */
export const browserStorageParseJson = (type, key) => {
	let item;

	try {
		item = JSON.parse(safeGetItem(type, key));
	} catch (err) {
		// Return undefined if the storage item is not valid JSON.
	}

	return item;
};
